import React, { Reducer, useEffect, useMemo, useReducer } from "react";
import { useTranslation } from "react-i18next";
import { colors, useMediaQuery } from "../../../Styles";
import { NewInput, LF_PENLIST, EditTerminalsCard } from "../..";
import { currentLfViewTypes, useLfContext } from "../_LocationFeeder/lfPenContext";
import { TerminalDataProps, terminalsData } from "../../GlobalComponents/_GlobalComponents/editTerminalsData";
import { SystemTypes } from "../../../Definitions";
import { Graph_QL, SectionsInfo, useDeviceInfo, useOldSection, useTerminals } from "../../../Services";
import { StoreAppDispatch, useAppSelector } from "../../../Store";
import { showSuccessWithTimeout } from "../../../Store/Thunks/successWithTimeout";

import EditSectionModal from "../../GlobalModals/editSectionModal";

import { useDispatch } from "react-redux";

import LocationPopper from "../../GlobalComponents/SubComponents/editTerminalsDataPopover";
import { ITerminalResContext, TerminalResContext, TerminalResDispatcherT, TerminalResReducer, useTerminalResContext } from "../../GlobalComponents/_GlobalComponents/terminalReducer";

interface NewSectionModal_Props {
  closeModal: () => void;
  section: SectionsInfo;
  focus_view?: boolean;
}

export default function EditLfSectionModal({ closeModal, section, focus_view = false }: NewSectionModal_Props) {
  const { t } = useTranslation();
  const mobile = useMediaQuery(`(max-width: 720px)`) || focus_view === true;
  const dispatchSuccessMode = useDispatch<StoreAppDispatch>();
  const { state, dispatch } = useLfContext();
  const { selectedPens, selectedSection, error } = state

  // #region | GraphQL
  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const { domain, connection } = useMemo(() => {
    let domain = Graph_QL.ObjToString({ domain: farm })
    let connection = Graph_QL.create({ domain: farm })
    return { domain, connection }
  }, [farm]);

  const { call: getDevices, data: deviceInfoState } = useDeviceInfo(connection);
  const { call: getTerminals, data: terminalsState, } = useTerminals(connection);
  const { maintainSectionLocations, deleteSectionLocations } = useOldSection(connection)

  useEffect(() => {
    getDevices({ fetchAmount: 500 }, connection);
    getTerminals({});
  }, [connection, getDevices, getTerminals])

  const terminals = useMemo(() => terminalsState[domain] && terminalsState[domain].data, [domain, terminalsState])
  const devices = useMemo(() => deviceInfoState[domain] && deviceInfoState[domain].data, [deviceInfoState, domain])
  // #endregion

  useEffect(() => {
    dispatch({ selectedSection: section, currentPenViewType: currentLfViewTypes.systemControle, selectedPens: [] })
    const newSelectedPens = selectedPens === undefined ? undefined : section.section_locations.filter((location) => selectedPens?.map((pen) => pen.location_id).includes(location.location_id))
    if (newSelectedPens?.length) { dispatch({ selectedPens: newSelectedPens }) }

  }, [section])

  const [termResState, termResDispatch] = useReducer<Reducer<ITerminalResContext, TerminalResDispatcherT>>(TerminalResReducer, {
    farm: farm,
    domain: domain,
    connection: connection,
    sectionName: section.name
  } as ITerminalResContext);

  // #region | Pen Data 
  const penData: TerminalDataProps[] = useMemo(() => {
    if (!terminals) return [];
    return Array.from(section.section_locations ?? []).map(currentPen => {
      const currentPenTerminals = terminals?.filter(t => t.fk_locations === currentPen.location_id) ?? [];
      const terminalres = terminalsData({ terminals: currentPenTerminals, devices, t });

      return {
        locationName: { text: t("locationFeederPages.sections.pen", {defaultValue: "Pen"}), value: currentPen.name ?? "" },
        terminals: terminalres,
        visual: currentPen.visual,
        location_id: currentPen.location_id
      };
    });
  }, [section.section_locations, terminals, devices]);
  // #endregion

  const handleTimeout = () => {
    setTimeout(() => {
      dispatch({ error: { errorText: "" } });
    }, 2000)
  }

  // #region | Pen View
  const penView = useMemo(() => {
    if (!selectedPens) return null;

    return selectedPens.map((pen, index) => {
      if (!pen) return null;

      const data = penData.find(p => p.location_id === pen.location_id);
      if (!data) return null;

      return (
        <EditTerminalsCard
          data={data}
          mobile={mobile}
          selected={pen}
          key={index}
          systemType={SystemTypes.freedaLf}
        />
      )
    });
  }, [selectedPens, penData]);
  // #endregion

  // const getAttentionForPen = (locationId: number): 0 | 2 => {
  // if (!termResState.updatedLocations || termResState.updatedLocations === undefined) return 0;

  // const hasLocationChange = termResState.updatedLocations?.[locationId] ? 2 : 0;

  // const hasTerminalChange = Object.values(termResState.terminalValues ?? []).some((t) =>
  //   terminals?.some(
  //     (term) =>
  //       term.terminal_id === t.terminal_id &&
  //       term.fk_locations === locationId &&
  //       (
  //         (t.port !== undefined && t.port !== term.port) ||
  //         (t.fk_devices !== undefined && t.fk_devices !== term.fk_devices) ||
  //         (t.configProgrammed !== undefined && t.configProgrammed !== term.programmed)
  //       )
  //   )
  // );

  // return hasLocationChange ? 2 : 0;
  // };

  // #region | Update Data
  const updateData = async () => {
    const sectionNames = Array.from(Object.values(termResState.sectionNames ?? []));
    const penNames = Array.from(Object.values(termResState.penNames ?? []));
    const terminalChanges = Array.from(Object.values(termResState.terminalValues ?? []))

    if (Object.values(sectionNames).some(s => !s.new_name?.trim())) {
      dispatch({
        error: {
          errorText: t("esfWalkThroughPages.newSection.penNameMissing", {
            defaultValue: "pen name missing"
          })
        }
      });
      handleTimeout();
      return;
    }

    if (sectionNames !== undefined || penNames.length || terminalChanges.length) {
      const response = await maintainSectionLocations(sectionNames, penNames, [], terminalChanges)

      if (!response.success) {
        dispatch({ error: { errorText: t("editFailed", { defaultValue: "edit failed" }) } })
        handleTimeout();
        return;
      }
      dispatchSuccessMode(showSuccessWithTimeout(`${sectionNames.length > 0 ? String(termResState.sectionNames?.[section.section_id].new_name) : section.name} ${t("wasUpdated", { defaultValue: "was updated" })}`));
      termResDispatch({ updatedLocations: [] })
      dispatch({ selectedSection: undefined, selectedPens: [] })
      closeModal();
    }
  };
  // #endregion

  return (<TerminalResContext.Provider value={{ state: termResState, dispatch: termResDispatch }}>
    <EditSectionModal
      systemType={SystemTypes.freedaLf}
      focus_view={focus_view}
      onClose={() => {
        termResDispatch({ updatedLocations: [] })
        dispatch({ selectedSection: undefined, selectedPens: [] })
        closeModal();
      }}
      subHeader={section.name}
      inputArea={
        <NewInput
          key={`name_${section.section_id}`}
          header={t("locationFeederPages.sections.sectionName", { defaultValue: "section name" })}
          onChange={(e) => { termResDispatch({ sectionNames: [{ section_id: section.section_id, new_name: String(e) }] }) }}
          isRequired
          valueOverrides={{
            defaultValue: section.name
          }}
        />
      }
      selectArea={selectedSection &&
        <LF_PENLIST
          key={selectedSection.section_id}
          focus_view={false}
          section={selectedSection}
          pens={selectedSection?.section_locations}
          popoverContent={(index) => {
            const pen = penData[index];
            return pen && <LocationPopper key={pen.location_id} locationData={[pen]} />;
          }}
          overrides={(index) => {
            const locationId = selectedSection.section_locations[index]?.location_id;

            return {
              primaryColor: colors.greyLightSecondary,
              fittedWidth: true,
              attention: termResState.updatedLocations?.[locationId] ? 5 : 0,
              key: locationId
            }
          }}
        />
      }
      activeArea={penView}
      onDeleteConfirm={
        async () => {
          const response = await deleteSectionLocations(section.section_id)

          if (!response) {
            dispatch({ error: { errorText: t("buttons.deleteFailed", { defaultValue: "delete failed" }) } })
            handleTimeout();
            return;
          }
          dispatchSuccessMode(showSuccessWithTimeout(`${section.name} ${t("wasDeleted", { defaultValue: "was deleted" })}`));
        }
      }
      onSave={() => updateData()}
      footerMessage={error.errorText}
    />
  </TerminalResContext.Provider>);
}
